import { connect } from 'react-redux';
import { get, isEqual, keyBy, pick } from 'lodash';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';

import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import {
  showConfirmationMessage,
  showErrorMessage,
  showSuccessMessage,
} from '@actions/messageconfirmation';

import SpinnerIcon from '@assets/icons/new-loader.svg';

import { Button, Tooltip } from '@commons/utils/styledLibraryComponents';
import { formatNewItemsOfDropdownSelection } from '@commons/utils/format';
import { getTheme } from '@commons/utils/theme';
import { LoadingIcon } from '@commons/Metrics/components/Stats/styledComponents';
import mixpanelUtils, { ENUM_EVENT_SOURCES, ENUM_EVENTS } from '@commons/utils/mixpanel';

import centralService from '@services/central';
import recipeService from '@services/recipe';

import { getEnableRecipeModalParams } from '@admin/products/recipes/utils/modalConfigurations';
import { getIsInCentralKitchenOnlyPage } from '@admin/utils/centralKitchen';
import { sendInventoryListTemplatesCreatedOnTheFlyMetrics } from '@admin/inventories/inventoryListTemplates/utils/metricsUtils';

import { formatCompositions } from '../RecipeComposition/utils/format';

import { Container } from './styledComponents';

/**
 * Assert composition of the recipe is valid
 *
 * @param {Array} compositions  - The components contained in the recipe
 *
 * @returns {boolean} Whether the form is valid
 */
export const areCompositionsValid = (compositions) =>
  !compositions.some(
    (composition) =>
      composition.entity.id === null || !composition.quantity || !composition.entity.unit,
  );

/**
 * Assert that the general form of that page can be saved (recipe)
 *
 * @param {Object} ingredient  - The recipe to save
 * @param {Array} composition  - The components contained in the recipe
 *
 * @returns {boolean} Whether the form is valid
 */
export const isFormValid = (recipe, compositions, clientHasMultipleBrands) => {
  if (
    !recipe.name ||
    !recipe.categoryId ||
    !recipe.value ||
    !recipe.unit ||
    !compositions.length ||
    (clientHasMultipleBrands && !recipe.brands)
  ) {
    return false;
  }

  return areCompositionsValid(compositions);
};

// Fields written by the metadata endpoint. The yield (`value`) is excluded on purpose: it lives on
// the mappings, so only the composition endpoint persists it.
const RECIPE_METADATA_FIELDS = [
  'img',
  'name',
  'unit',
  'recipePreparationSteps',
  'brands',
  'inventoryListTemplates',
  'hasStock',
  'batchProduction',
  'hasLoss',
  'hasTransfer',
  'categoryId',
];

const _hasMetadataChanged = (recipe, initialRecipe, clientHasMultipleChannels) => {
  const fieldsToPick = clientHasMultipleChannels
    ? [...RECIPE_METADATA_FIELDS, 'hasMultipleChannels']
    : RECIPE_METADATA_FIELDS;

  return !isEqual(pick(recipe, fieldsToPick), pick(initialRecipe, fieldsToPick));
};

const _hasProductionChanged = (
  productionStoreEntityMappings,
  initialProductionStoreEntityMappings,
) => {
  if (productionStoreEntityMappings == null || initialProductionStoreEntityMappings == null) {
    return false;
  }

  return !isEqual(
    productionStoreEntityMappings.map(({ storeId }) => storeId),
    initialProductionStoreEntityMappings.map(({ storeId }) => storeId),
  );
};

const _haveCompositionLinesChanged = (compositions, initialCompositions) => {
  const formattedCompositionById = compositions.reduce(
    (acc, { entity: { id, name }, quantity, channelIds }) => {
      acc[id] = { name, quantity, channelIds };
      return acc;
    },
    {},
  );

  const formattedInitialCompositionById = initialCompositions.reduce(
    (acc, { entity: { id, name }, quantity, channels }) => {
      const channelIds = channels.map(({ id }) => id);

      acc[id] = { name, quantity, channelIds };

      return acc;
    },
    {},
  );

  return !isEqual(formattedCompositionById, formattedInitialCompositionById);
};

const _buildCompositionComparable = (composition = []) =>
  composition.reduce((acc, { entity: { id }, quantity, channelIds = [] }) => {
    acc[id] = { quantity, channelIds: [...channelIds].sort() };

    return acc;
  }, {});

const _hasVariantCompositionChanged = (variant, initialVariant) =>
  !isEqual(
    _buildCompositionComparable(variant.composition),
    _buildCompositionComparable(initialVariant.composition),
  );

const _haveVariantsChanged = (recipeVariants, initialRecipeVariants, deletedVariantIds) => {
  if (deletedVariantIds.length > 0) {
    return true;
  }

  if (recipeVariants.length !== initialRecipeVariants.length) {
    return true;
  }

  if (recipeVariants.some(({ isNew }) => isNew)) {
    return true;
  }

  const initialVariantById = keyBy(initialRecipeVariants, 'id');

  return recipeVariants.some((variant) => {
    const initialVariant = initialVariantById[variant.id];

    if (!initialVariant) {
      return true;
    }

    const hasStoresChanged = !isEqual(
      [...(variant.storeIds ?? [])].sort(),
      [...(initialVariant.storeIds ?? [])].sort(),
    );

    const hasCompositionChanged = _hasVariantCompositionChanged(variant, initialVariant);

    return variant.name !== initialVariant.name || hasStoresChanged || hasCompositionChanged;
  });
};

/**
 * Splits what changed between the two writers of a recipe, since each has its own endpoint. The
 * composition endpoint versions everything it writes, so it must only be called when the composition
 * really changed — calling it on every save would open a version and republish the recipe
 * calculations for nothing.
 *
 * @returns {{ hasMetadataChanged: boolean, hasCompositionChanged: boolean }}
 */
export const getRecipeChanges = ({
  recipe,
  initialRecipe,
  compositions,
  initialCompositions,
  productionStoreEntityMappings,
  initialProductionStoreEntityMappings,
  recipeVariants = [],
  initialRecipeVariants = [],
  deletedVariantIds = [],
  clientHasMultipleChannels,
}) => {
  const hasMetadataChanged =
    _hasMetadataChanged(recipe, initialRecipe, clientHasMultipleChannels) ||
    _hasProductionChanged(productionStoreEntityMappings, initialProductionStoreEntityMappings);

  // Toggling multi-channel relays every mapping across the client channels, so the composition has
  // to be rewritten even when no line was touched
  const hasChannelsFlagChanged =
    clientHasMultipleChannels && recipe.hasMultipleChannels !== initialRecipe.hasMultipleChannels;

  const hasCompositionChanged =
    _haveCompositionLinesChanged(compositions, initialCompositions) ||
    !isEqual(recipe.value, initialRecipe.value) ||
    hasChannelsFlagChanged ||
    _haveVariantsChanged(recipeVariants, initialRecipeVariants, deletedVariantIds);

  return { hasMetadataChanged, hasCompositionChanged };
};

/**
 * Stores taken by a variant created in the same save do not make an update of the variant that
 * lost them: the user only performed a creation
 */
const _isVariantUpdated = (variant, initialVariant, storeIdsClaimedByNewVariants) => {
  if (variant.name !== initialVariant.name) {
    return true;
  }

  if (_hasVariantCompositionChanged(variant, initialVariant)) {
    return true;
  }

  const storeIds = variant.storeIds ?? [];
  const initialStoreIds = initialVariant.storeIds ?? [];

  const hasGainedStores = storeIds.some((storeId) => !initialStoreIds.includes(storeId));

  const hasLostStoresOutsideCreations = initialStoreIds.some(
    (storeId) => !storeIds.includes(storeId) && !storeIdsClaimedByNewVariants.includes(storeId),
  );

  return hasGainedStores || hasLostStoresOutsideCreations;
};

const _sendRecipeVariantsMetrics = ({ recipeId, recipeVariants, initialRecipeVariants }) => {
  const newVariants = recipeVariants.filter(({ isNew }) => isNew);
  const storeIdsClaimedByNewVariants = newVariants.flatMap(({ storeIds }) => storeIds ?? []);
  const initialVariantById = keyBy(initialRecipeVariants, 'id');

  const hasUpdatedVariant = recipeVariants.some((variant) => {
    const initialVariant = initialVariantById[variant.id];

    if (!initialVariant) {
      return false;
    }

    return _isVariantUpdated(variant, initialVariant, storeIdsClaimedByNewVariants);
  });

  if (newVariants.length > 0) {
    mixpanelUtils.sendMetric(ENUM_EVENTS.CREATE_RECIPE_VARIANT, { recipeId });
  }

  if (hasUpdatedVariant) {
    mixpanelUtils.sendMetric(ENUM_EVENTS.UPDATE_RECIPE_VARIANT, { recipeId });
  }
};

const areVariantsValid = (recipeVariants) =>
  recipeVariants.every((variant) => {
    if (!variant.name || !variant.name.trim()) {
      return false;
    }

    if (!variant.composition || !variant.composition.length) {
      return false;
    }

    return areCompositionsValid(variant.composition);
  });

/**
 * Maps the local variant state to the recipe save payload. New variants (`isNew`) omit their id
 * so the back creates them
 */
const formatVariantsPayload = (recipeVariants) =>
  recipeVariants.map((variant) => {
    const compositions = variant.composition.map(
      ({ entity: { id: entityId }, quantity, channelIds }) => {
        const composition = { entityId, quantity };

        if (channelIds && channelIds.length) {
          composition.channelIds = channelIds;
        }

        return composition;
      },
    );

    const basePayload = {
      name: variant.name,
      storeIds: variant.storeIds,
      compositions,
    };

    if (variant.isNew) {
      return basePayload;
    }

    return { id: variant.id, ...basePayload };
  });

export const createRecipe = async (
  clientId,
  recipe,
  compositions,
  recipeVariants,
  showMessage,
  history,
  dispatchers,
) => {
  dispatchers.savingInProgress();

  const {
    img,
    name,
    value,
    unit,
    hasMultipleChannels,
    hasStock,
    hasLoss,
    hasTransfer,
    batchProduction,
    recipePreparationSteps,
    brands,
    inventoryListTemplates,
    categoryId,
  } = recipe;

  try {
    const brandIds = brands.map(({ id }) => id);

    const payload = {
      img,
      name,
      unit,
      value,
      hasMultipleChannels,
      hasStock,
      hasLoss,
      hasTransfer,
      batchProduction,
      compositions,
      recipePreparationSteps,
      brandIds,
      inventoryListTemplates: formatNewItemsOfDropdownSelection(inventoryListTemplates),
      categoryId,
      variants: formatVariantsPayload(recipeVariants),
    };

    getIsInCentralKitchenOnlyPage()
      ? await centralService.createKitchenRecipe(clientId, payload)
      : await recipeService.create(clientId, payload);

    _sendRecipeVariantsMetrics({ recipeVariants, initialRecipeVariants: [] });

    sendInventoryListTemplatesCreatedOnTheFlyMetrics(
      inventoryListTemplates,
      ENUM_EVENT_SOURCES.RECIPE_DETAILS,
    );

    showMessage(i18next.t('ADMIN.RECIPES.CREATE_SUCCESS'));

    history.goBack();
  } catch (err) {
    // Check if error is due to duplicate recipe name
    if (
      err.response.status === 409 &&
      get(err, 'response.data.message', '').includes('already used in another recipe')
    ) {
      showMessage(i18next.t('ADMIN.RECIPES.DUPLICATE_NAME_ERROR'), 'error');
    } else {
      showMessage(i18next.t('ADMIN.RECIPES.CREATE_FAILURE'), 'error');
    }
  } finally {
    dispatchers.savingFinished();
  }
};

/**
 * Patches the metadata of a recipe, the central kitchen screen going through its own endpoint since
 * it also remaps the production stores.
 */
const _patchRecipeMetadata = (metadataPayload, productionStoreEntityMappings) => {
  if (!getIsInCentralKitchenOnlyPage()) {
    return recipeService.patchById(metadataPayload);
  }

  const storeIds = productionStoreEntityMappings.map(({ storeId }) => storeId);

  return centralService.patchKitchenRecipeById({ ...metadataPayload, storeIds });
};

/**
 * The two scopes of a recipe are saved through two requests, so a failure on the composition one
 * leaves the metadata already persisted: saying so keeps the user from reading a plain failure and
 * losing track of what actually went through. Retrying the save is safe, both endpoints being
 * no-ops for an unchanged payload.
 */
const _getUpdateFailureMessage = (err, hasPersistedMetadata) => {
  const isDuplicateNameError =
    err.response?.status === 409 &&
    get(err, 'response.data.message', '').includes('already used in another recipe');

  if (isDuplicateNameError) {
    return i18next.t('ADMIN.RECIPES.DUPLICATE_NAME_ERROR');
  }

  if (hasPersistedMetadata) {
    return i18next.t('ADMIN.RECIPES.MODIFY_COMPOSITION_FAILURE');
  }

  return i18next.t('ADMIN.RECIPES.MODIFY_FAILURE');
};

/**
 * Saves an existing recipe through the two endpoints that own it, each only when its own scope
 * changed. Metadata goes first: the composition endpoint regenerates the details pdf from the stored
 * metadata, and reads `hasMultipleChannels` on the recipe to lay the mappings out per channel.
 */
export const updateRecipe = async ({
  recipe,
  compositions,
  productionStoreEntityMappings,
  recipeVariants,
  initialRecipeVariants,
  deletedVariantIds,
  hasMetadataChanged,
  hasCompositionChanged,
  showMessage,
  history,
  dispatchers,
  onSaveSuccess,
}) => {
  dispatchers.savingInProgress();

  const {
    id,
    img,
    recipePreparationSteps,
    name,
    value,
    unit,
    hasMultipleChannels,
    hasStock,
    hasLoss,
    hasTransfer,
    batchProduction,
    brands,
    inventoryListTemplates,
    categoryId,
  } = recipe;

  let hasPersistedMetadata = false;

  try {
    const brandIds = brands.map(({ id: brandId }) => brandId);

    const metadataPayload = {
      id,
      img,
      name,
      unit,
      value,
      hasMultipleChannels,
      hasStock,
      hasLoss,
      hasTransfer,
      batchProduction,
      recipePreparationSteps,
      brandIds,
      inventoryListTemplates: formatNewItemsOfDropdownSelection(inventoryListTemplates),
      categoryId,
    };

    if (hasMetadataChanged) {
      await _patchRecipeMetadata(metadataPayload, productionStoreEntityMappings);

      hasPersistedMetadata = true;

      sendInventoryListTemplatesCreatedOnTheFlyMetrics(
        inventoryListTemplates,
        ENUM_EVENT_SOURCES.RECIPE_DETAILS,
      );
    }

    if (hasCompositionChanged) {
      await recipeService.updateComposition({
        id,
        value,
        compositions,
        variants: formatVariantsPayload(recipeVariants),
        deletedVariantIds,
      });

      _sendRecipeVariantsMetrics({ recipeId: id, recipeVariants, initialRecipeVariants });
    }

    showMessage(i18next.t('ADMIN.RECIPES.MODIFY_SUCCESS'));

    if (onSaveSuccess) {
      try {
        await onSaveSuccess();
      } catch {
        // Best effort: a failed re-sync must not mask the successful save
      }
    }

    history.goBack();
  } catch (err) {
    showErrorMessage(_getUpdateFailureMessage(err, hasPersistedMetadata));
  } finally {
    dispatchers.savingFinished();
  }
};

/**
 * Handle the save of the entire form (recipe)
 *
 * @param {Props} props - The props linked to the component
 *
 * @returns {void}
 */
export const handleSave = async (props) => {
  const {
    user,
    compositions: propsCompositions,
    recipe,
    showMessage,
    history,
    savingInProgress,
    savingFinished,
    recipeComponentsForm,
    productionStoreEntityMappings,
    channels,
    recipeVariants,
    initialRecipeVariants,
    deletedVariantIds,
    selectedVariantId,
    refreshVariantsAfterSave,
  } = props;

  // The shared form mirrors the selected tab. When a variant tab is active the form holds the
  // variant's composition, so the main recipe's composition must be read from `compositions`
  // state (kept in sync by the detail page) instead of the form.
  const { recipeComponents: formEntities } = recipeComponentsForm.getValues();

  const mainComposition =
    selectedVariantId == null
      ? formatCompositions(formEntities, propsCompositions, channels)
      : propsCompositions;

  const formattedCompositions = mainComposition.map(
    ({ entity: { id: entityId, category, name }, quantity, channelIds, allergens }) => ({
      entityId,
      quantity,
      channelIds,
      allergens,
      category,
      name,
    }),
  );

  if (recipe.id) {
    // Diffed against the composition about to be sent rather than against the props state, so an
    // edit still in the form cannot be missed
    const { hasMetadataChanged, hasCompositionChanged } = getRecipeChanges({
      ...props,
      compositions: mainComposition,
    });

    return updateRecipe({
      recipe,
      compositions: formattedCompositions,
      productionStoreEntityMappings,
      recipeVariants,
      initialRecipeVariants,
      deletedVariantIds,
      hasMetadataChanged,
      hasCompositionChanged,
      showMessage,
      history,
      dispatchers: {
        savingInProgress,
        savingFinished,
      },
      onSaveSuccess: refreshVariantsAfterSave,
    });
  }

  await createRecipe(
    get(user, 'clientId'),
    recipe,
    formattedCompositions,
    recipeVariants,
    showMessage,
    history,
    {
      savingInProgress,
      savingFinished,
    },
  );
};

const renderAuditHistoryTrigger = ({ auditButtonLabel, auditButtonLoading, onOpenAuditDrawer }) => {
  if (auditButtonLoading) {
    return <LoadingIcon alt={'component-loader'} height="16px" src={SpinnerIcon} width="16px" />;
  }

  if (!auditButtonLabel) {
    return null;
  }

  const theme = getTheme();

  return (
    <Button
      buttonCustomStyle={{
        paddingLeft: '0px',
        height: '40px',
        font: theme.fonts.textSmallHeight14,
      }}
      color={'inpulse-outline'}
      fontColor={theme.colors.blacks.ipBlack4}
      handleClick={onOpenAuditDrawer}
      icon={'/images/inpulse/history.svg'}
      iconOnLeft={false}
      label={auditButtonLabel}
      noBorder
    />
  );
};

export const RecipeDetailsBottomBar = (props) => {
  const {
    recipe,
    compositions,
    isReadOnly,
    isSaveDisabled,
    onRecipeChange,
    openGenericModal,
    pageLoading,
    pageLoaded,
    showSuccessMessage,
    showErrorMessage,
    initialCompositions,
    initialRecipe,
    initialProductionStoreEntityMappings,
    productionStoreEntityMappings,
    clientHasMultipleBrands,
    clientHasMultipleChannels,
    recipeVariants = [],
    initialRecipeVariants = [],
    deletedVariantIds = [],
    auditButtonLabel,
    auditButtonLoading,
    onOpenAuditDrawer,
  } = props;

  const auditHistoryTrigger = renderAuditHistoryTrigger({
    auditButtonLabel,
    auditButtonLoading,
    onOpenAuditDrawer,
  });

  const [formIsValid, setFormIsValid] = useState(false);

  useEffect(() => {
    const formIsValid =
      isFormValid(recipe, compositions, clientHasMultipleBrands) &&
      areVariantsValid(recipeVariants);

    const { hasMetadataChanged, hasCompositionChanged } = getRecipeChanges({
      recipe,
      initialRecipe,
      compositions,
      initialCompositions,
      productionStoreEntityMappings,
      initialProductionStoreEntityMappings,
      recipeVariants,
      initialRecipeVariants,
      deletedVariantIds,
      clientHasMultipleChannels,
    });

    setFormIsValid(!isSaveDisabled && formIsValid && (hasMetadataChanged || hasCompositionChanged));
  }, [
    recipe,
    compositions,
    isSaveDisabled,
    productionStoreEntityMappings,
    recipeVariants,
    initialRecipeVariants,
    deletedVariantIds,
  ]);

  const enableRecipe = async (recipeToEnable) => {
    const recipeToEnableId = recipeToEnable.id;

    pageLoading();

    try {
      await recipeService.enableRecipe(recipeToEnableId);
      onRecipeChange({ ...recipe, active: true });
      showSuccessMessage(i18next.t('ADMIN.RECIPES.ENABLE_SUCCESS'));
    } catch {
      showErrorMessage(i18next.t('ADMIN.RECIPES.ENABLE_ERROR'));
    } finally {
      pageLoaded();
    }
  };

  const isEnableButtonDisabled = compositions.some(({ entity: { active } }) => !active);

  if (recipe.active || !recipe.id) {
    return (
      <Container>
        {auditHistoryTrigger}
        <Button
          color={'inpulse-default'}
          handleClick={() => formIsValid && handleSave(props)}
          icon={'/images/inpulse/save-white-small.svg'}
          isDisabled={!formIsValid || isReadOnly}
          label={i18next.t('GENERAL.SAVE')}
        />
      </Container>
    );
  }

  if (isEnableButtonDisabled) {
    return (
      <Container>
        {auditHistoryTrigger}
        <Tooltip
          displayBigger={true}
          place={'left'}
          renderTooltipTrigger={() => (
            <Button
              color={'inpulse-default'}
              handleClick={() => {}}
              icon={'/images/inpulse/power-white-small.svg'}
              isDisabled={true}
              label={i18next.t('GENERAL.ACTIVATE')}
            />
          )}
          text={i18next.t('ADMIN.RECIPES.ENABLED_DISABLE_TOOLTIP')}
        />
      </Container>
    );
  }

  return (
    <Container>
      {auditHistoryTrigger}
      <Button
        color={'inpulse-default'}
        handleClick={() => {
          const params = getEnableRecipeModalParams(recipe, enableRecipe);
          openGenericModal(params);
        }}
        icon={'/images/inpulse/power-white-small.svg'}
        label={i18next.t('GENERAL.ACTIVATE')}
      />
    </Container>
  );
};

const mapStateToProps = (state) => ({
  user: state.baseReducer.user,
});

const mapDispatchToProps = (dispatch) => ({
  openGenericModal: (params) => {
    dispatch(openGenericModal(params));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
  showMessage: (message, type) => {
    dispatch(showConfirmationMessage(message, type));
  },
  showSuccessMessage: (message) => {
    dispatch(showSuccessMessage(message));
  },
  showErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  savingInProgress: () => {
    dispatch(loading());
  },
  savingFinished: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(RecipeDetailsBottomBar);
